import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useCallable } from '../lib/useCallable';
import { useCollection } from '../lib/useFirestore';
import StatusBadge from '../components/StatusBadge';

export default function MatterList() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { run, busy, error } = useCallable();
  const [selectedMatterIds, setSelectedMatterIds] = useState([]);
  const [confirmingMatters, setConfirmingMatters] = useState(null);
  const [deleteSummary, setDeleteSummary] = useState('');
  const mine = useCollection('matters', [['where', 'createdBy', '==', user.uid]]);
  // Matters someone else owns but has shared with me (see the "Shared with" card on
  // MatterDetail.jsx and the collaboratorUids array-contains read rule in firestore.rules).
  const shared = useCollection('matters', [['where', 'collaboratorUids', 'array-contains', user.uid]]);
  /**
   * Your own templates, for the "start from one" picker.
   *
   * The `where` is load-bearing, not a convenience filter. Templates became private in 1.81.0 and
   * the read rule is scoped to the creator; Firestore does not narrow a query to what the rules
   * permit, it REFUSES one whose results it cannot guarantee. So an unfiltered read here does not
   * return fewer rows — it returns none, and the picker is silently empty. That is exactly what
   * happened: the query in Templates.jsx was updated and this second one was missed.
   */
  const templates = useCollection('templates', [
    ['where', 'createdBy', '==', user.uid],
    ['orderBy', 'createdAt', 'desc'],
  ]);

  // "My matters" is exactly that for everyone, approvers and admins included: matters you
  // submitted, plus matters shared with you. Firm-wide visibility already lives on its own
  // dedicated pages — the Approval Queue (matters pending your own action) and Workflow
  // Tracking (firm-wide reporting for admins/viewers) — so this page never needs to browse
  // every matter in the firm.
  const rows = mine.data.slice().sort(sortByCreated);
  const sharedRows = shared.data.filter((m) => m.createdBy !== user.uid).slice().sort(sortByCreated);

  const startFromTemplate = (templateId) => {
    const template = templates.data.find((t) => t.id === templateId);
    if (!template) return;
    navigate('/matters/new', {
      state: {
        seedMatter: template.data || {},
        seedLabel: `Pre-filled from the template “${template.name}”.`,
      },
    });
  };

  const toggleMatter = (matterId) => {
    setSelectedMatterIds((selected) =>
      selected.includes(matterId) ? selected.filter((id) => id !== matterId) : [...selected, matterId]
    );
  };

  const requestDelete = (matters) => {
    setDeleteSummary('');
    setConfirmingMatters(matters);
  };

  const confirmDelete = async () => {
    const matters = confirmingMatters || [];
    const result = await run('deleteMatters', { matterIds: matters.map((matter) => matter.id) });
    if (!result) return;

    const { deleted = [], skipped = [] } = result;
    setSelectedMatterIds((selected) => selected.filter((id) => !matters.some((matter) => matter.id === id)));
    setConfirmingMatters(null);
    setDeleteSummary(formatDeleteSummary(deleted, skipped));
  };

  const selectedMatters = rows.filter((matter) => selectedMatterIds.includes(matter.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-firm-700 dark:text-firm-100">My matters</h1>
        <div className="flex items-center gap-2">
          {templates.data.length > 0 && (
            <select
              className="rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
              value=""
              onChange={(e) => startFromTemplate(e.target.value)}
            >
              <option value="">Start from template…</option>
              {templates.data.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
          <Link
            to="/matters/new"
            className="rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400"
          >
            New matter
          </Link>
        </div>
      </div>

      {deleteSummary && (
        <p
          role="status"
          className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200"
        >
          {deleteSummary}
        </p>
      )}
      {error && (
        <p
          role="alert"
          className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
        >
          Unable to delete the selected matter{selectedMatters.length === 1 ? '' : 's'}: {error}
        </p>
      )}
      {selectedMatters.length > 0 && (
        <div className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm text-red-900 dark:text-red-100">
            {selectedMatters.length} matter{selectedMatters.length === 1 ? '' : 's'} selected
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => requestDelete(selectedMatters)}
            className="rounded bg-red-700 px-3 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Delete selected ({selectedMatters.length})
          </button>
        </div>
      )}

      <MatterTable
        rows={rows}
        emptyMessage="No matters yet. Start one with “New matter”."
        user={user}
        isAdmin={isAdmin}
        selectedMatterIds={selectedMatterIds}
        onToggleMatter={toggleMatter}
        onRequestDelete={(matter) => requestDelete([matter])}
        deleting={busy}
      />

      {sharedRows.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-firm-700 dark:text-firm-100">Shared with me</h2>
          <MatterTable
            rows={sharedRows}
            showSharedBadge
            user={user}
            isAdmin={isAdmin}
            selectedMatterIds={selectedMatterIds}
            onToggleMatter={toggleMatter}
            onRequestDelete={(matter) => requestDelete([matter])}
            deleting={busy}
          />
        </div>
      )}

      {confirmingMatters && (
        <DeleteConfirmationDialog
          matters={confirmingMatters}
          busy={busy}
          onCancel={() => setConfirmingMatters(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

function MatterTable({
  rows,
  emptyMessage,
  showSharedBadge,
  user,
  isAdmin,
  selectedMatterIds,
  onToggleMatter,
  onRequestDelete,
  deleting,
}) {
  if (rows.length === 0) {
    return (
      <p className="rounded-lg bg-white dark:bg-slate-900 p-6 text-sm text-slate-500 dark:text-slate-400 shadow">{emptyMessage}</p>
    );
  }
  return (
    <table className="w-full overflow-hidden rounded-lg bg-white dark:bg-slate-900 text-sm shadow">
      <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        <tr>
          <th className="w-12 px-4 py-2">
            <span className="sr-only">Select</span>
          </th>
          <th className="px-4 py-2">Client / matter</th>
          <th className="px-4 py-2">Numbers</th>
          <th className="px-4 py-2">Status</th>
          <th className="px-4 py-2">Updated</th>
          <th className="px-4 py-2" />
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
        {rows.map((matter) => (
          <tr key={matter.id}>
            <td className="px-4 py-2">
              {canDeleteMatter(matter, user, isAdmin) && (
                <input
                  type="checkbox"
                  aria-label={`Select ${matterLabel(matter)} for deletion`}
                  checked={selectedMatterIds.includes(matter.id)}
                  onChange={() => onToggleMatter(matter.id)}
                  className="h-4 w-4 rounded border-slate-300 text-red-700 focus:ring-red-600 dark:border-slate-600"
                />
              )}
            </td>
            <td className="px-4 py-2">
              <div className="font-medium text-slate-800 dark:text-slate-100">
                {matter.client?.name || <em className="text-slate-400 dark:text-slate-500">Untitled</em>}
                {showSharedBadge && (
                  <span className="ml-2 rounded bg-firm-50 dark:bg-firm-900 px-1.5 py-0.5 text-xs font-medium text-firm-700 dark:text-firm-100">
                    Shared
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {matter.filesToBeOpened?.matterName || '—'}
              </div>
            </td>
            <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-300">
              {[matter.clientNumber, matter.matterNumber].filter(Boolean).join(' / ') || '—'}
            </td>
            <td className="px-4 py-2">
              <StatusBadge status={matter.status} />
            </td>
            <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">{formatTs(matter.updatedAt)}</td>
            <td className="px-4 py-2 text-right">
              <Link
                to={matter.status === 'draft' ? `/matters/${matter.id}/edit` : `/matters/${matter.id}`}
                className="text-firm-700 dark:text-firm-100 hover:underline"
              >
                {matter.status === 'draft' ? 'Continue' : 'View'}
              </Link>
              {canDeleteMatter(matter, user, isAdmin) && (
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => onRequestDelete(matter)}
                  className="ml-3 text-xs text-red-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-300"
                >
                  Delete
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DeleteConfirmationDialog({ matters, busy, onCancel, onConfirm }) {
  const count = matters.length;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-matters-title"
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-slate-900"
      >
        <h2 id="delete-matters-title" className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Delete {count} matter{count === 1 ? '' : 's'}?
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          This permanently deletes the matter{count === 1 ? '' : 's'}, all approval and attachment
          records, and every uploaded or generated file. This cannot be undone.
        </p>
        <ul className="mt-3 max-h-40 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-slate-700 dark:text-slate-200">
          {matters.map((matter) => (
            <li key={matter.id}>{matterLabel(matter)}</li>
          ))}
        </ul>
        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="rounded bg-red-700 px-3 py-2 text-sm font-medium text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Deleting…' : `Delete ${count} matter${count === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function canDeleteMatter(matter, user, isAdmin) {
  return Boolean(isAdmin || (matter.createdBy === user?.uid && matter.status === 'draft'));
}

function matterLabel(matter) {
  const name = matter.client?.name || matter.filesToBeOpened?.matterName || 'Untitled matter';
  const numbers = [matter.clientNumber, matter.matterNumber].filter(Boolean).join(' / ');
  return numbers ? `${name} (${numbers})` : name;
}

function formatDeleteSummary(deleted, skipped) {
  if (skipped.length === 0) {
    return `Deleted ${deleted.length} matter${deleted.length === 1 ? '' : 's'}.`;
  }

  const deletedPart = `Deleted ${deleted.length} of ${deleted.length + skipped.length} matters`;
  const detail = skipped.map((item) => `${item.matterId}: ${item.reason}`).join('; ');
  return `${deletedPart}. ${skipped.length} skipped: ${detail}.`;
}

function sortByCreated(a, b) {
  return (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0);
}

export function formatTs(ts) {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString();
}
